import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Plus, 
  Calendar, 
  Tag, 
  Users, 
  Clock, 
  CheckCircle, 
  AlertTriangle, 
  BarChart2, 
  Target,
  ArrowLeft,
  Edit3,
  Trash2,
  Eye,
  PlusCircle,
  X,
  ChevronDown,
  ChevronRight,
  PlayCircle,
  Info
} from 'react-feather';
import { releasesApi, featureTagApi, projectApi, taskApi } from '../services/api';
import { getProgressColor } from '../utils/projectUtils';
import { validateReleaseCompletion, createStatusChangeHandler, getNextStatus, getStatusChangeButtonLabel, shouldShowStatusChangeButton, getStatusChangeButtonIcon, isReleaseReadyForRelease } from '../utils/releaseUtils';
import ReadyForReleaseBadge from '../components/Releases/ReadyForReleaseBadge';
import FeatureTagDetailModal from '../components/FeatureTags/FeatureTagDetailModal';
import { IconButton } from '../components/ui/IconButton';
import './ReleaseManagement.css';

const ReleaseManagement = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  
  // State management
  const [releases, setReleases] = useState([]);
  const [featureTags, setFeatureTags] = useState([]);
  const [project, setProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // UI state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingRelease, setEditingRelease] = useState(null);
  const [expandedCards, setExpandedCards] = useState(new Set());
  const [selectedFeatureTag, setSelectedFeatureTag] = useState(null);
  const [showFeatureModal, setShowFeatureModal] = useState(false);
  
  // Form state
  const [newRelease, setNewRelease] = useState({
    name: '',
    description: '',
    version: '',
    target_date: '',
    status: 'planned'
  });
  

  // Load data on component mount
  useEffect(() => {
    loadData();
  }, [projectId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [releasesData, featureTagsData, projectData, tasksData] = await Promise.all([
        releasesApi.getProjectReleases(projectId),
        featureTagApi.getByProject(projectId),
        projectApi.get(projectId),
        taskApi.getByProject(projectId)
      ]);
      
      setReleases(releasesData);
      setFeatureTags(featureTagsData);
      setProject(projectData);
      setTasks(tasksData || []);
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load release data');
    } finally {
      setLoading(false);
    }
  };

  // Calculate project-level insights
  const projectInsights = useMemo(() => {
    const totalReleases = releases.length;
    const completedReleases = releases.filter(r => r.status === 'completed').length;
    const inProgressReleases = releases.filter(r => r.status === 'in_progress').length;
    const plannedReleases = releases.filter(r => r.status === 'planned').length;
    
    const totalFeatures = releases.reduce((sum, release) => sum + (release.release_feature_tags?.length || 0), 0);
    const totalTasks = releases.reduce((sum, release) => {
      return sum + (release.insights?.total_tasks || 0);
    }, 0);
    const completedTasks = releases.reduce((sum, release) => {
      return sum + (release.insights?.completed_tasks || 0);
    }, 0);
    
    const avgCompletion = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
    
    return {
      totalReleases,
      completedReleases,
      inProgressReleases,
      plannedReleases,
      totalFeatures,
      totalTasks,
      completedTasks,
      avgCompletion
    };
  }, [releases]);

  // Handle create release
  const handleCreateRelease = async (e) => {
    e.preventDefault();
    try {
      const releaseData = {
        ...newRelease,
        target_date: newRelease.target_date || null
      };
      
      const createdRelease = await releasesApi.createRelease(projectId, releaseData);
      setReleases(prev => [createdRelease, ...prev]);
      setShowCreateModal(false);
      setNewRelease({
        name: '',
        description: '',
        version: '',
        target_date: '',
        status: 'planned'
      });
    } catch (err) {
      console.error('Error creating release:', err);
      setError('Failed to create release');
    }
  };

  // Handle update release
  const handleUpdateRelease = async (e) => {
    e.preventDefault();
    
    // Find the release being edited to get its feature tags
    const currentRelease = releases.find(r => r.id === editingRelease.id);
    
    // Validate that all tasks are completed before marking release as completed
    const validation = validateReleaseCompletion(currentRelease, tasks, editingRelease.status);
    if (!validation.isValid) {
      setError(validation.errorMessage);
      return;
    }
    
    try {
      setError(null);
      const payload = {
        ...editingRelease,
        target_date: editingRelease.target_date && String(editingRelease.target_date).trim() !== '' ? editingRelease.target_date : null
      };
      const updatedRelease = await releasesApi.updateRelease(editingRelease.id, payload);
      setReleases(prev => prev.map(r => r.id === updatedRelease.id ? updatedRelease : r));
      setEditingRelease(null);
    } catch (err) {
      console.error('Error updating release:', err);
      // Check if it's a validation error from the backend
      if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else {
        setError('Failed to update release');
      }
    }
  };

  // Handle quick status change
  const handleQuickStatusChange = (releaseId, newStatus) => {
    const currentRelease = releases.find(r => r.id === releaseId);
    if (!currentRelease) return;
    
    const handler = createStatusChangeHandler(
      currentRelease,
      tasks,
      (updatedRelease) => {
        setReleases(prev => prev.map(r => r.id === updatedRelease.id ? updatedRelease : r));
        setError(null);
      },
      (errorMessage) => {
        setError(errorMessage);
      }
    );
    
    handler(newStatus);
  };

  // Handle delete release
  const handleDeleteRelease = async (releaseId) => {
    if (window.confirm('Are you sure you want to delete this release?')) {
      try {
        await releasesApi.deleteRelease(releaseId);
        setReleases(prev => prev.filter(r => r.id !== releaseId));
      } catch (err) {
        console.error('Error deleting release:', err);
        setError('Failed to delete release');
      }
    }
  };


  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return '#10b981';
      case 'in_progress': return '#f59e0b';
      case 'planned': return '#6b7280';
      case 'cancelled': return '#ef4444';
      default: return '#6b7280';
    }
  };

  // Handle card expansion toggle
  const handleCardExpand = (releaseId, e) => {
    e.stopPropagation();
    setExpandedCards(prev => {
      const newSet = new Set(prev);
      if (newSet.has(releaseId)) {
        newSet.delete(releaseId);
      } else {
        newSet.add(releaseId);
      }
      return newSet;
    });
  };

  // Get feature tag progress data from insights
  const getFeatureTagProgress = (release, featureTagId) => {
    if (!release?.insights?.feature_tags) return null;
    return release.insights.feature_tags.find(ft => ft.id === featureTagId);
  };

  if (loading) {
    return (
      <div className="release-management">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading release data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="release-management">
        <div className="error-container">
          <AlertTriangle size={48} />
          <h3>Error</h3>
          <p>{error}</p>
          <button onClick={loadData} className="retry-button">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="release-management">
      {/* Header */}
      <div className="release-management__header">
        <div className="release-management__header-left">
          <button 
            className="back-button"
            onClick={() => navigate(`/projects/${projectId}`)}
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1>Release Management{project?.name ? ` - ${project.name}` : ''}</h1>
            <p>Plan and manage releases for your project</p>
          </div>
        </div>
        <button 
          className="create-release-button"
          onClick={() => setShowCreateModal(true)}
        >
          <Plus size={20} />
          Create Release
        </button>
      </div>

      {/* Project Insights */}
      <div className="release-management__insights">
        <h2>Project Overview</h2>
        <div className="insights-grid">
          <div className="insight-card">
            <div className="insight-icon">
              <Target size={24} />
            </div>
            <div className="insight-content">
              <div className="insight-value">{projectInsights.totalReleases}</div>
              <div className="insight-label">Total Releases</div>
            </div>
          </div>
          
          <div className="insight-card">
            <div className="insight-icon">
              <CheckCircle size={24} />
            </div>
            <div className="insight-content">
              <div className="insight-value">{projectInsights.completedReleases}</div>
              <div className="insight-label">Completed</div>
            </div>
          </div>
          
          <div className="insight-card">
            <div className="insight-icon">
              <Clock size={24} />
            </div>
            <div className="insight-content">
              <div className="insight-value">{projectInsights.inProgressReleases}</div>
              <div className="insight-label">In Progress</div>
            </div>
          </div>
          
          <div className="insight-card">
            <div className="insight-icon">
              <Tag size={24} />
            </div>
            <div className="insight-content">
              <div className="insight-value">{projectInsights.totalFeatures}</div>
              <div className="insight-label">Total Features</div>
            </div>
          </div>
          
          <div className="insight-card">
            <div className="insight-icon">
              <BarChart2 size={24} />
            </div>
            <div className="insight-content">
              <div className="insight-value">{projectInsights.avgCompletion}%</div>
              <div className="insight-label">Avg Completion</div>
            </div>
          </div>
        </div>
      </div>

      {/* Releases List */}
      <div className="release-management__releases">
        <h2>Releases</h2>
        {releases.length === 0 ? (
          <div className="empty-state">
            <Target size={48} />
            <h3>No releases yet</h3>
            <p>Create your first release to start planning features</p>
            <button 
              className="create-release-button"
              onClick={() => setShowCreateModal(true)}
            >
              <Plus size={20} />
              Create Release
            </button>
          </div>
        ) : (
          <div className="releases-grid">
            {releases.map(release => {
              const isExpanded = expandedCards.has(release.id);
              const featureTags = release.release_feature_tags || [];
              
              return (
              <div key={release.id} className="release-card">
                <div className="release-card__header">
                  <div className="release-card__title">
                    <h3>{release.name}</h3>
                    <span className="release-version">v{release.version}</span>
                  </div>
                  <div className="release-card__actions">
                      {featureTags.length > 0 && (
                        <button 
                          className="action-button"
                          onClick={(e) => handleCardExpand(release.id, e)}
                          title={isExpanded ? "Collapse Features" : "Expand Features"}
                        >
                          {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                        </button>
                      )}
                      {shouldShowStatusChangeButton(release.status) && (
                        <button 
                          className="action-button status-change-button"
                          onClick={() => handleQuickStatusChange(release.id, getNextStatus(release.status))}
                          title={getStatusChangeButtonLabel(release.status)}
                        >
                          {release.status === 'planned' ? (
                            <PlayCircle size={16} />
                          ) : (
                            <CheckCircle size={16} />
                          )}
                        </button>
                      )}
                    <button 
                      className="action-button"
                      onClick={() => navigate(`/projects/${projectId}/releases/${release.id}`)}
                      title="View Details"
                    >
                      <Eye size={16} />
                    </button>
                    <button 
                      className="action-button"
                      onClick={() => setEditingRelease(release)}
                      title="Edit Release"
                    >
                      <Edit3 size={16} />
                    </button>
                    <button 
                      className="action-button danger"
                      onClick={() => handleDeleteRelease(release.id)}
                      title="Delete Release"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
                
                <div className="release-card__content">
                  <div className="release-card__status">
                    <div 
                      className="status-indicator"
                      style={{ backgroundColor: getStatusColor(release.status) }}
                    />
                    <span className="status-text">{release.status.replace('_', ' ')}</span>
                    {isReleaseReadyForRelease(release, tasks) && <ReadyForReleaseBadge />}
                  </div>
                  
                  {release.description && (
                    <p className="release-description">{release.description}</p>
                  )}
                  
                  <div className="release-card__meta">
                    <div className="meta-item">
                      <Calendar size={16} />
                      <span>Target: {formatDate(release.target_date)}</span>
                    </div>
                    <div className="meta-item">
                      <Tag size={16} />
                        <span>{featureTags.length} features</span>
                    </div>
                  </div>
                  
                  {release.insights && (
                    <div className="release-card__progress">
                      <div className="progress-header">
                        <span className="progress-label">Progress</span>
                        <span className="progress-percentage">
                          {release.insights.completion_percentage || 0}%
                        </span>
                      </div>
                      <div className="progress-bar">
                        <div 
                          className="progress-fill"
                          style={{ 
                            width: `${release.insights.completion_percentage || 0}%`,
                            backgroundColor: getProgressColor(release.insights.completion_percentage || 0)
                          }}
                        />
                      </div>
                      <div className="progress-details">
                        <span className="progress-text">
                          {release.insights.completed_tasks || 0} of {release.insights.total_tasks || 0} tasks completed
                        </span>
                      </div>
                    </div>
                  )}

                    {/* Expanded Features Section */}
                    {isExpanded && featureTags.length > 0 && (
                      <div className="release-card__features-expanded">
                        <div className="release-features-header">
                          <Tag size={16} />
                          <span>Features ({featureTags.length})</span>
                        </div>
                        <div className="release-features-list">
                          {featureTags.map(rft => {
                            const featureTag = rft.feature_tag;
                            const progressData = getFeatureTagProgress(release, featureTag.id);
                            const completionPercentage = progressData?.completion_percentage || 0;
                            const taskCount = progressData?.task_count || 0;
                            const completedTasks = progressData?.completed_tasks || 0;
                            
                            return (
                              <div key={rft.id} className="release-feature-item">
                                <div className="release-feature-info">
                                  <div 
                                    className="release-feature-color"
                                    style={{ backgroundColor: featureTag.color }}
                                  />
                                  <div className="release-feature-details">
                                    <div className="release-feature-details-row">
                                      <span className="release-feature-name">{featureTag.name}</span>
                                      <IconButton
                                        variant="neutral-tertiary"
                                        className="release-feature-details-icon"
                                        icon={<Info size={14} />}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setSelectedFeatureTag(featureTag);
                                          setShowFeatureModal(true);
                                        }}
                                        title="View feature details"
                                        aria-label="View feature details"
                                      />
                                    </div>
                                    {featureTag.due_date && (
                                      <span className="release-feature-due-date">
                                        Due: {formatDate(featureTag.due_date)}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                {progressData && taskCount > 0 && (
                                  <div className="release-feature-progress">
                                    <div className="release-feature-progress-info">
                                      <span className="release-feature-tasks">
                                        {completedTasks}/{taskCount} tasks
                                      </span>
                                      <span className="release-feature-percentage">
                                        {completionPercentage.toFixed(0)}%
                                      </span>
                                    </div>
                                    <div className="release-feature-progress-bar">
                                      <div 
                                        className="progress-fill"
                                        style={{ 
                                          width: `${completionPercentage}%`,
                                          backgroundColor: getProgressColor(completionPercentage)
                                        }}
                                      />
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Create Release Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal__header">
              <h2>Create New Release</h2>
              <button 
                className="modal__close"
                onClick={() => setShowCreateModal(false)}
              >
                <X size={20} />
              </button>
            </div>
            
            <form onSubmit={handleCreateRelease} className="modal__content">
              <div className="form-group">
                <label htmlFor="name">Release Name *</label>
                <input
                  type="text"
                  id="name"
                  value={newRelease.name}
                  onChange={(e) => setNewRelease(prev => ({ ...prev, name: e.target.value }))}
                  required
                  placeholder="e.g., Q1 2024 Release"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="version">Version *</label>
                <input
                  type="text"
                  id="version"
                  value={newRelease.version}
                  onChange={(e) => setNewRelease(prev => ({ ...prev, version: e.target.value }))}
                  required
                  placeholder="e.g., 1.0.0"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  value={newRelease.description}
                  onChange={(e) => setNewRelease(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Describe what this release includes..."
                  rows={3}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="target_date">Target Date</label>
                <input
                  type="date"
                  id="target_date"
                  value={newRelease.target_date}
                  onChange={(e) => setNewRelease(prev => ({ ...prev, target_date: e.target.value }))}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="status">Status</label>
                <select
                  id="status"
                  value={newRelease.status}
                  onChange={(e) => setNewRelease(prev => ({ ...prev, status: e.target.value }))}
                >
                  <option value="planned">Planned</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
              
              <div className="modal__actions">
                <button 
                  type="button" 
                  className="cancel-button"
                  onClick={() => setShowCreateModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="submit-button">
                  Create Release
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Release Modal */}
      {editingRelease && (
        <div className="modal-overlay" onClick={() => setEditingRelease(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal__header">
              <h2>Edit Release</h2>
              <button 
                className="modal__close"
                onClick={() => setEditingRelease(null)}
              >
                <X size={20} />
              </button>
            </div>
            
            <form onSubmit={handleUpdateRelease} className="modal__content">
              <div className="form-group">
                <label htmlFor="edit_name">Release Name *</label>
                <input
                  type="text"
                  id="edit_name"
                  value={editingRelease.name}
                  onChange={(e) => setEditingRelease(prev => ({ ...prev, name: e.target.value }))}
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="edit_version">Version *</label>
                <input
                  type="text"
                  id="edit_version"
                  value={editingRelease.version}
                  onChange={(e) => setEditingRelease(prev => ({ ...prev, version: e.target.value }))}
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="edit_description">Description</label>
                <textarea
                  id="edit_description"
                  value={editingRelease.description || ''}
                  onChange={(e) => setEditingRelease(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="edit_target_date">Target Date</label>
                <input
                  type="date"
                  id="edit_target_date"
                  value={editingRelease.target_date ? editingRelease.target_date.split('T')[0] : ''}
                  onChange={(e) => setEditingRelease(prev => ({ ...prev, target_date: e.target.value }))}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="edit_status">Status</label>
                <select
                  id="edit_status"
                  value={editingRelease.status}
                  onChange={(e) => setEditingRelease(prev => ({ ...prev, status: e.target.value }))}
                >
                  <option value="planned">Planned</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
              
              <div className="modal__actions">
                <button 
                  type="button" 
                  className="cancel-button"
                  onClick={() => setEditingRelease(null)}
                >
                  Cancel
                </button>
                <button type="submit" className="submit-button">
                  Update Release
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showFeatureModal && selectedFeatureTag && (
        <FeatureTagDetailModal
          tag={selectedFeatureTag}
          isOpen={showFeatureModal}
          onClose={() => {
            setSelectedFeatureTag(null);
            setShowFeatureModal(false);
          }}
          tasks={tasks.filter(t => t.feature_tag?.id === selectedFeatureTag?.id)}
          users={[]}
        />
      )}
    </div>
  );
};

export default ReleaseManagement;
